import PageHeader from "./PageHeader";
import { useSiteContent } from "../../context/SiteContext";
import { useAudio } from "../../context/AudioContext";
import { Play, Pause, Disc3, ArrowUpRight } from "lucide-react";

function TrackRow({ track }) {
  const audio = useAudio();
  const isCurrent = track.audioUrl && audio.src === track.audioUrl;
  const isPlaying = isCurrent && audio.playing;
  return (
    <div className="flex items-center gap-3 py-1.5 group/track">
      <button
        onClick={() => {
          if (!track.audioUrl) return;
          isCurrent ? audio.toggle() : audio.play(track.audioUrl);
        }}
        disabled={!track.audioUrl}
        className={`flex-shrink-0 p-1.5 border transition-colors ${
          track.audioUrl
            ? isPlaying
              ? "border-neon text-neon"
              : "border-[#27272A] text-neutral-400 hover:border-neon hover:text-neon"
            : "border-[#1A1A1A] text-neutral-700 cursor-not-allowed"
        }`}
        aria-label={track.audioUrl ? `${isPlaying ? "Pause" : "Play"} ${track.title}` : "No preview"}
        title={track.audioUrl ? "Preview" : "No preview available"}
      >
        {isPlaying ? <Pause size={12} /> : <Play size={12} />}
      </button>
      <span className="label-mono text-neutral-600 w-6 text-right flex-shrink-0">
        {String(track.trackNum).padStart(2, "0")}
      </span>
      <span className={`text-sm flex-1 min-w-0 truncate ${isCurrent ? "text-neon" : "text-neutral-300"}`}>
        {track.title}
      </span>
      {track.duration && (
        <span className="label-mono text-neutral-600 flex-shrink-0">{track.duration}</span>
      )}
    </div>
  );
}

export default function Soundscape() {
  const content = useSiteContent();
  return (
    <div data-testid="page-soundscape">
      <PageHeader
        code="04"
        label="SOUNDSCAPE"
        title="Soundscape"
        subtitle="The complete release catalog. Every drop pinned to the same wall, sorted by year. Pick a cover, follow the thread."
      />

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {content.releases.map((r) => {
              const platforms = Object.entries(r.streaming || {}).filter(([, url]) => url);
              const primaryUrl = platforms[0]?.[1] || "#";
              const tracks = r.tracks || [];

              return (
                <div
                  key={r.id}
                  data-testid={`release-${r.id}`}
                  className="em-card group"
                >
                  <a
                    href={primaryUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-[#111111] to-[#0A0A0A]">
                      {r.coverUrl ? (
                        <img
                          src={r.coverUrl}
                          alt={r.title}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#00FF6A]/20 to-transparent blur-2xl" />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="font-display font-black text-6xl text-neon/30">
                                {r.title.split(" ").map(w => w[0]).join("").slice(0, 2)}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
                      <div className="absolute top-3 left-3 label-mono text-neon border border-neon/40 px-2 py-1 bg-[#0A0A0A]/70">
                        {r.type}
                      </div>
                      <div className="absolute top-3 right-3 label-mono text-neutral-300 border border-[#27272A] px-2 py-1 bg-[#0A0A0A]/70">
                        {r.year}
                      </div>
                      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-neon text-black p-3 inline-flex">
                          <Play size={16} />
                        </div>
                      </div>
                    </div>
                  </a>
                  <div className="p-5 border-t border-[#27272A]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display font-bold text-lg group-hover:text-neon transition-colors">
                          {r.title}
                        </div>
                        <div className="label-mono text-neutral-500 mt-1">
                          {r.trackCount} TRK · {r.runtime}
                        </div>
                      </div>
                      <Disc3 size={20} className="text-neutral-500 group-hover:text-neon group-hover:rotate-90 transition-all" />
                    </div>

                    {tracks.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-[#1A1A1A] divide-y divide-[#141414]">
                        {tracks.map((t) => (
                          <TrackRow key={t.id} track={t} />
                        ))}
                      </div>
                    )}

                    {platforms.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {platforms.map(([platform, url]) => (
                          <a
                            key={platform}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="label-mono text-xs border border-[#27272A] px-2 py-1 hover:border-neon hover:text-neon transition-colors inline-flex items-center gap-1"
                          >
                            {platform.replace(/_/g, " ").toUpperCase()}
                            <ArrowUpRight size={10} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-neutral-500 text-xs mt-8 label-mono">
            // {content.releases.length} release{content.releases.length !== 1 ? "s" : ""} catalogued
          </p>
        </div>
      </section>
    </div>
  );
}
